import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bars3Icon, 
  BellIcon,
  CalculatorIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';

const Navbar = ({ toggleSidebar, isCollapsed }) => {
  const { jeweller, signOut, currentUser } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [newEnquiriesCount, setNewEnquiriesCount] = useState(0);
  const [projectsUnderwayCount, setProjectsUnderwayCount] = useState(0);
  const notificationRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get first name (fallbacks to "there")
  const firstName = jeweller?.firstName 
    || jeweller?.name?.split(' ')[0] 
    || 'there';

  // Use brandIcon instead of profilePhoto
  const brandIcon = jeweller?.brandIcon || null;

  // Get company initials for avatar fallback (from company name)
  const companyInitials = jeweller?.companyName 
    ? jeweller.companyName.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase()
    : 'JW';

  // Listen for new enquiries
  useEffect(() => {
    if (!currentUser?.uid) return;

    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef, 
      where('jewellerId', '==', currentUser.uid),
      where('status', '==', 'new_enquiry')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNewEnquiriesCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Listen for projects underway
  useEffect(() => {
    if (!currentUser?.uid) return;

    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef, 
      where('jewellerId', '==', currentUser.uid),
      where('status', '==', 'project_started')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProjectsUnderwayCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewEnquiries = () => {
    setNotificationOpen(false);
    navigate('/enquiries');
  };

  return (
    <header 
      className="fixed top-0 right-0 z-30 transition-all duration-300 ease-in-out"
      style={{
        height: '64px',
        backgroundColor: 'var(--Surface-Primary)',
        borderBottom: '1px solid var(--Stroke-Light)',
        boxShadow: 'var(--shadow-sm)',
        left: isCollapsed ? '80px' : '264px',
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div className="h-full flex items-center justify-between" 
        style={{ paddingLeft: 'var(--spacing-xl)', paddingRight: 'var(--spacing-xl)' }}
      >
        {/* Left side */}
        <div className="flex items-center" style={{ gap: 'var(--spacing-l)' }}>
          {/* Mobile menu button */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden"
            style={{
              padding: 'var(--spacing-xs)',
              borderRadius: 'var(--radius-xs)',
              color: 'var(--Text-Secondary)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Grey-100)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>

          {/* Welcome message */}
          <div className="hidden md:block">
            <h1 style={{
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)',
              lineHeight: 'var(--line-height-subtitle2)'
            }}>
              {newEnquiriesCount > 0 
                ? `You have ${newEnquiriesCount === 1 ? 'a new enquiry!' : `${newEnquiriesCount} new enquiries!`}`
                : projectsUnderwayCount > 0
                ? `You have ${projectsUnderwayCount} project${projectsUnderwayCount === 1 ? '' : 's'} underway`
                : `Welcome back, ${firstName}!`
              }
            </h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center" style={{ gap: 'var(--spacing-s)' }}>
          {/* Notifications with dropdown */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setNotificationOpen(!notificationOpen)}
              className="relative"
              style={{
                padding: 'var(--spacing-xs)',
                borderRadius: 'var(--radius-xs)',
                color: 'var(--Text-Secondary)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--Grey-100)';
                e.currentTarget.style.color = 'var(--Text-Primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--Text-Secondary)';
              }}
            >
              <BellIcon className="h-5 w-5" />
              {newEnquiriesCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: 'var(--Red-600)',
                  borderRadius: '50%',
                  border: '2px solid var(--Surface-Primary)'
                }} />
              )}
            </button>

            {/* Notification Dropdown */}
            {notificationOpen && (
              <div 
                className="absolute right-0 z-50 animate-fadeIn"
                style={{
                  marginTop: 'var(--spacing-xs)',
                  width: '280px',
                  backgroundColor: 'var(--Surface-Primary)',
                  borderRadius: 'var(--radius-s)',
                  boxShadow: 'var(--shadow-popup)',
                  border: '1px solid var(--Stroke-Light)',
                  padding: 'var(--spacing-m)',
                }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}>
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: newEnquiriesCount > 0 ? 'var(--brand-gradient-4)' : 'var(--Grey-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 'var(--spacing-m)'
                  }}>
                    <BellIcon 
                      className="h-6 w-6" 
                      style={{ 
                        color: newEnquiriesCount > 0 ? 'var(--Grey-900)' : 'var(--Text-Tertiary)' 
                      }} 
                    />
                  </div>

                  {/* Message */}
                  <p style={{
                    fontSize: 'var(--font-size-body3)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--Text-Primary)',
                    marginBottom: 'var(--spacing-xs)'
                  }}>
                    {newEnquiriesCount > 0 
                      ? `You have ${newEnquiriesCount} new enquir${newEnquiriesCount === 1 ? 'y' : 'ies'}`
                      : 'No new notifications'}
                  </p>

                  {newEnquiriesCount > 0 && (
                    <p style={{
                      fontSize: 'var(--font-size-body5)',
                      color: 'var(--Text-Tertiary)',
                      marginBottom: 'var(--spacing-m)'
                    }}>
                      Review and respond to customer enquiries
                    </p>
                  )}

                  {/* View Now Button */}
                  {newEnquiriesCount > 0 && (
                    <button
                      onClick={handleViewEnquiries}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-xs) var(--spacing-m)',
                        backgroundColor: 'var(--Grey-900)',
                        color: 'var(--Text-Contrast)',
                        borderRadius: 'var(--radius-xl)',
                        border: 'none',
                        fontSize: 'var(--font-size-body4)',
                        fontWeight: 'var(--font-weight-medium)',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--Grey-700)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--Grey-900)';
                      }}
                    >
                      View Now
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Calculator */}
          <button 
            onClick={() => navigate('/calculator')}
            className="hidden sm:block"
            style={{
              padding: 'var(--spacing-xs)',
              borderRadius: 'var(--radius-xs)',
              color: 'var(--Text-Secondary)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Grey-100)';
              e.currentTarget.style.color = 'var(--Text-Primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--Text-Secondary)';
            }}
            title="Calculator"
          >
            <CalculatorIcon className="h-5 w-5" />
          </button>

          {<button
            onClick={() => navigate('/refer-jeweller')}
            className="hidden sm:flex items-center"
            style={{
              gap: 'var(--spacing-xs)',
              padding: 'var(--spacing-xs) var(--spacing-s)',
              borderRadius: 'var(--radius-full)',
              color: 'var(--Text-Primary)',
              backgroundColor: 'var(--Surface-Secondary)',
              border: '1px solid var(--Stroke-Light)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Purple-50)';
              e.currentTarget.style.borderColor = 'var(--Purple-200)';
              e.currentTarget.style.color = 'var(--Purple-700)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
              e.currentTarget.style.borderColor = 'var(--Stroke-Light)';
              e.currentTarget.style.color = 'var(--Text-Primary)';
            }}
            title="Invite a jeweller | Earn £100"
          >
            <span>Invite a jeweller | Earn £50</span>
          </button>}

          {/* Divider */}
          <div style={{
            height: '32px',
            width: '1px',
            backgroundColor: 'var(--Stroke-Light)',
            marginLeft: 'var(--spacing-xs)',
            marginRight: 'var(--spacing-xs)'
          }} />

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center group"
              style={{
                gap: 'var(--spacing-s)',
                padding: 'var(--spacing-xs)',
                paddingRight: 'var(--spacing-s)',
                borderRadius: 'var(--radius-xs)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Brand Icon Avatar */}
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-xs)',
                background: brandIcon ? 'var(--Surface-Primary)' : 'var(--brand-gradient-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--Text-Contrast)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)',
                boxShadow: 'var(--shadow-sm)',
                overflow: 'hidden',
                border: brandIcon ? '1px solid var(--Stroke-Light)' : 'none'
              }}>
                {brandIcon ? (
                  <img 
                    src={brandIcon} 
                    alt={jeweller?.companyName || "Company"}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  companyInitials
                )}
              </div>

              {/* Company name and email */}
              <div className="hidden sm:block text-left">
                <p style={{
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--Text-Primary)',
                  lineHeight: 'var(--line-height-body4)'
                }}>
                  {jeweller?.companyName || 'Company Name'}
                </p>
                <p style={{
                  fontSize: 'var(--font-size-body5)',
                  color: 'var(--Text-Tertiary)',
                  lineHeight: 'var(--line-height-body5)'
                }}>
                  {jeweller?.email || 'email@example.com'}
                </p>
              </div>

              {/* Dropdown arrow */}
              <ChevronDownIcon 
                style={{
                  width: '16px',
                  height: '16px',
                  color: 'var(--Text-Tertiary)',
                  transition: 'transform 0.2s ease',
                  transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div 
                className="absolute right-0 z-50 animate-fadeIn"
                style={{
                  marginTop: 'var(--spacing-xs)',
                  width: '224px',
                  backgroundColor: 'var(--Surface-Primary)',
                  borderRadius: 'var(--radius-s)',
                  boxShadow: 'var(--shadow-popup)',
                  border: '1px solid var(--Stroke-Light)',
                  paddingTop: 'var(--spacing-xs)',
                  paddingBottom: 'var(--spacing-xs)'
                }}
              >
                {/* User info */}
                <div style={{
                  paddingLeft: 'var(--spacing-m)',
                  paddingRight: 'var(--spacing-m)',
                  paddingTop: 'var(--spacing-s)',
                  paddingBottom: 'var(--spacing-s)',
                  borderBottom: '1px solid var(--Stroke-Light)'
                }}>
                  <p style={{
                    fontSize: 'var(--font-size-body4)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--Text-Primary)',
                    marginBottom: '2px'
                  }}>
                    {`${jeweller?.firstName || ''} ${jeweller?.surname || ''}`.trim() || jeweller?.name || 'Jeweller Name'}
                  </p>
                  <p className="truncate" style={{
                    fontSize: 'var(--font-size-body5)',
                    color: 'var(--Text-Tertiary)'
                  }}>
                    {jeweller?.email || 'email@example.com'}
                  </p>
                </div>

                {/* Menu items */}
                <div style={{ paddingTop: 'var(--spacing-xs)', paddingBottom: 'var(--spacing-xs)' }}>
                  <a
                    href="/account"
                    className="flex items-center"
                    style={{
                      gap: 'var(--spacing-s)',
                      paddingLeft: 'var(--spacing-m)',
                      paddingRight: 'var(--spacing-m)',
                      paddingTop: 'var(--spacing-xs)',
                      paddingBottom: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-body4)',
                      color: 'var(--Text-Primary)',
                      textDecoration: 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <UserCircleIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
                    Account
                  </a>

                  <a
                    href="/feedback"
                      className="flex items-center"
                      style={{
                        gap: 'var(--spacing-s)',
                        paddingLeft: 'var(--spacing-m)',
                        paddingRight: 'var(--spacing-m)',
                        paddingTop: 'var(--spacing-xs)',
                        paddingBottom: 'var(--spacing-xs)',
                        fontSize: 'var(--font-size-body4)',
                        color: 'var(--Text-Primary)',
                        textDecoration: 'none',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
                      Feedback & Suggest
                  </a>

                  <a
                    href="/documents"
                    className="flex items-center"
                    style={{
                      gap: 'var(--spacing-s)',
                      paddingLeft: 'var(--spacing-m)',
                      paddingRight: 'var(--spacing-m)',
                      paddingTop: 'var(--spacing-xs)',
                      paddingBottom: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-body4)',
                      color: 'var(--Text-Primary)',
                      textDecoration: 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <DocumentTextIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
                    Documents
                  </a>

                  <a
                    href="/contact-support"
                    className="flex items-center"
                    style={{
                      gap: 'var(--spacing-s)',
                      paddingLeft: 'var(--spacing-m)',
                      paddingRight: 'var(--spacing-m)',
                      paddingTop: 'var(--spacing-xs)',
                      paddingBottom: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-body4)',
                      color: 'var(--Text-Primary)',
                      textDecoration: 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LifebuoyIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
                    Contact Support
                  </a>
                </div>

                {/* Sign out */}
                <div style={{
                  borderTop: '1px solid var(--Stroke-Light)',
                  paddingTop: 'var(--spacing-xs)'
                }}>
                  <button
                    onClick={signOut}
                    className="flex items-center w-full text-left"
                    style={{
                      gap: 'var(--spacing-s)',
                      paddingLeft: 'var(--spacing-m)',
                      paddingRight: 'var(--spacing-m)',
                      paddingTop: 'var(--spacing-xs)',
                      paddingBottom: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-body4)',
                      color: 'var(--Red-600)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--Red-50)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
